import { create } from "@bufbuild/protobuf";

import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import {
  CreateSourceRequest,
  CreateSourceRequestSchema,
  CreateSourceResponse,
} from "@gx/ts-contracts/services/source/api/source_pb";
import { SourceConfigurationSchema } from "@gx/ts-contracts/services/source/resources/source_configuration/source_configuration_pb";
import { SourceWebhookConfigurationSchema } from "@gx/ts-contracts/services/source/resources/source_configuration/webhook/webhook_pb";

import {
  DEFAULT_SIGNATURE_HEADER,
  SOURCE_WEBHOOK_CONFIGURATION_CASE,
  WEBHOOK_BASE_URL,
} from "@/pages/sources/components/create/sources/webhook/constants";
import {
  CreateSourceWebhookPhase,
  CreateSourceWebhookState,
  WebhookCredentials,
} from "@/pages/sources/components/create/sources/webhook/types";

export interface WebhookSourceProvisioningResult {
  sourceId: string;
  credentials: WebhookCredentials;
}

export function buildCreateSourceWebhookRequest(
  state: CreateSourceWebhookState,
): CreateSourceRequest {
  return create(CreateSourceRequestSchema, {
    name: state.sourceName.trim(),
    type: SourceType.WEBHOOK,
    configuration: create(SourceConfigurationSchema, {
      configuration: {
        case: SOURCE_WEBHOOK_CONFIGURATION_CASE,
        value: create(SourceWebhookConfigurationSchema, {
          signatureHeader: state.signatureHeader || DEFAULT_SIGNATURE_HEADER,
          hmacAlgorithm: "HMAC-SHA256",
          hmacKeyNonce: new Uint8Array(),
          hmacKeyEncrypted: new Uint8Array(),
          schemaFingerprint: "",
          webhookIdentifier: "",
          batchIntervalSeconds: state.batchIntervalSeconds ?? undefined,
          batchMaxSize: state.batchMaxSize ?? undefined,
        }),
      },
    }),
  });
}

export function extractWebhookProvisioningResult(
  response: CreateSourceResponse,
): WebhookSourceProvisioningResult {
  const sourceId = response.source?.id;
  if (!sourceId) {
    throw new Error("Create source response did not include a source id");
  }

  const configResponse = response.configurationResponse;
  if (!configResponse || configResponse.details.case !== "webhookDetails") {
    throw new Error("Create source response did not include webhook provisioning details");
  }

  const details = configResponse.details.value;
  if (!details.endpointIdentifier) {
    throw new Error("Create source response did not include endpoint identifier");
  }

  if (!details.webhookSecret) {
    throw new Error("Create source response did not include webhook secret");
  }

  const webhookUrl = `${WEBHOOK_BASE_URL}/v1/${details.endpointIdentifier}`;

  return {
    sourceId,
    credentials: {
      webhookUrl,
      webhookSecret: details.webhookSecret,
    },
  };
}

export function isWebhookInProgress(phase: CreateSourceWebhookPhase): boolean {
  return phase === CreateSourceWebhookPhase.CREATING;
}

export function generateCurlCommand(
  webhookUrl: string,
  signatureHeader: string,
  signature = "<signature>",
  payload = '{"test": "data"}',
): string {
  let compactPayload = payload;
  try {
    compactPayload = JSON.stringify(JSON.parse(payload));
  } catch {
    // use as-is if not valid JSON
  }
  return `curl -X POST \\
  -H "Content-Type: application/json" \\
  -H "${signatureHeader}: ${signature}" \\
  -d '${compactPayload}' \\
  "${webhookUrl}"`;
}

export async function computeHmacSignature(secretBase64: string, payload: string): Promise<string> {
  // Handle both standard and URL-safe base64
  const normalized = secretBase64.replace(/-/g, "+").replace(/_/g, "/");
  const keyBytes = Uint8Array.from(atob(normalized), (c) => c.charCodeAt(0));
  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const payloadBytes = new TextEncoder().encode(payload);
  const signatureBuffer = await window.crypto.subtle.sign("HMAC", cryptoKey, payloadBytes);
  return Array.from(new Uint8Array(signatureBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
